import React, { useState } from "react";
import { VEG_TYPES } from "@/content/veguk";
import { TYPES, PDF } from "@/constants/testIds";
import { ChevronRight, Download } from "lucide-react";

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/pdf/definitions`;

export const VegTypes = () => {
  const [active, setActive] = useState(VEG_TYPES[0].code);
  const current = VEG_TYPES.find((t) => t.code === active) || VEG_TYPES[0];

  return (
    <section
      id="types"
      data-testid={TYPES.root}
      className="py-24 lg:py-32 bg-[#EAE3D9]/60 relative"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <span className="veg-overline">The five vegetarian types</span>
          <h2 className="font-serif-display mt-4 text-4xl sm:text-5xl text-[#2E5C46] leading-[1.05]">
            One word, many traditions.
          </h2>
          <p className="mt-5 text-[#4A5D4E] leading-relaxed">
            VegUK recognises five distinct vegetarian types in the UK, each with its own
            dietary boundaries. Tap a code to read the full definition.
          </p>
          <a
            data-testid={PDF.downloadTypes}
            href={PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[#2E5C46] hover:text-[#234836]"
          >
            <Download size={16} />
            Download the print-ready definitions PDF
          </a>
        </div>

        {/* Type cards grid — visual identity blocks */}
        <div className="mt-14 grid grid-cols-2 md:grid-cols-5 gap-4">
          {VEG_TYPES.map((t) => {
            const isActive = t.code === active;
            return (
              <button
                key={t.code}
                data-testid={TYPES.card(t.code)}
                onClick={() => setActive(t.code)}
                className={`veg-card-hover relative text-left rounded-lg border p-6 transition-all ${
                  isActive
                    ? "border-[#2C352D] shadow-[0_18px_40px_-25px_rgba(44,53,45,0.4)]"
                    : "border-[#D1C9B9]"
                }`}
                style={{ backgroundColor: t.bg }}
              >
                <div
                  className="font-serif-display text-5xl leading-none font-medium"
                  style={{ color: t.color }}
                >
                  {t.code}
                </div>
                <div className="mt-6">
                  <div className="text-sm font-medium text-[#2C352D]">
                    {t.name}
                  </div>
                  <div className="text-xs text-[#4A5D4E] mt-1 leading-relaxed">
                    {t.tagline}
                  </div>
                </div>
                {isActive && (
                  <span
                    className="absolute top-3 right-3 inline-flex items-center text-[10px] uppercase tracking-[0.18em]"
                    style={{ color: t.color }}
                  >
                    Active
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Active type detail panel */}
        <div className="mt-10 bg-white border border-[#D1C9B9] rounded-lg overflow-hidden grid lg:grid-cols-12">
          <div
            className="lg:col-span-4 p-10 lg:p-12 flex flex-col justify-between"
            style={{ backgroundColor: current.bg }}
          >
            <div>
              <div
                className="font-serif-display text-[7rem] leading-none font-medium"
                style={{ color: current.color }}
              >
                {current.code}
              </div>
              <p
                className="mt-2 text-sm tracking-[0.18em] uppercase"
                style={{ color: current.color }}
              >
                {current.name}
              </p>
            </div>
            <p
              className="font-serif-display text-2xl italic mt-10"
              style={{ color: current.color }}
            >
              “{current.tagline}”
            </p>
          </div>
          <div className="lg:col-span-8 p-10 lg:p-14">
            <span className="veg-overline">Definition</span>
            <p className="font-serif-display text-3xl text-[#2C352D] mt-4 leading-[1.2]">
              {current.short}
            </p>
            <p className="mt-6 text-[#4A5D4E] leading-relaxed text-base">
              {current.long}
            </p>
            <div className="mt-10 flex items-center gap-2 text-sm text-[#2E5C46]">
              <span>Browse other types</span>
              <ChevronRight size={16} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
